// Calibración continua de fichas: cruza el consumo REAL histórico (orden_consumos)
// contra el teórico por materia prima y propone ajustar la cantidad de las fichas
// que la usan. Cierra el lazo del costeo teórico vs real. Catering → Resumen.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SlidersHorizontal, History, Loader2, Check, X, TrendingDown, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, num } from '@/features/inventario/shared';
import { catRpc, invalidateCatering, money } from './shared';
import { LOCALE } from '@/lib/utils/regional';

const isoHoy = (): string => { const d = new Date(); return d.toISOString().slice(0, 10); };
const isoHace = (dias: number): string => { const d = new Date(); d.setDate(d.getDate() - dias); return d.toISOString().slice(0, 10); };

interface Item {
  componente_id: string; material: string; unidad: string;
  ordenes: number; n_desvio: number; plan_total: number; real_total: number;
  ratio: number | null; desvio_pct: number; dispersion: number;
  costo_unit: number; impacto_costo: number; n_lineas: number; recetas: string[] | null;
  factor_sugerido: number | null; semaforo: 'ok' | 'revisar' | 'calibrar'; recomendacion: string;
}
interface Resp {
  periodo: { desde: string; hasta: string; min_muestras: number; tolerancia: number };
  items: Item[];
  resumen: { materiales: number; a_calibrar: number; a_revisar: number; impacto_total: number };
}
interface Hist {
  id: string; fecha: string; material: string; factor: number; ratio_obs: number | null;
  lineas: number; ordenes: number; n_desvio: number; motivo: string | null; usuario: string | null;
}

const SEM: Record<string, { cls: string; txt: string; label: string }> = {
  calibrar: { cls: 'border-red-500 bg-red-50 dark:bg-red-900/20', txt: 'text-red-700 dark:text-red-400', label: 'Calibrar' },
  revisar: { cls: 'border-amber-500 bg-amber-50 dark:bg-amber-900/20', txt: 'text-amber-700 dark:text-amber-400', label: 'Revisar' },
  ok: { cls: 'border-muted bg-muted/40', txt: 'text-muted-foreground', label: 'OK' },
};
const fmtFecha = (iso: string): string => new Date(iso).toLocaleDateString(LOCALE, { day: '2-digit', month: '2-digit', year: '2-digit' });

export function CalibracionCatering() {
  const qc = useQueryClient();
  const [desde, setDesde] = useState(isoHace(90));
  const [hasta, setHasta] = useState(isoHoy());
  const [minMuestras, setMinMuestras] = useState(3);
  const [tolerancia, setTolerancia] = useState(5);
  const [soloAcc, setSoloAcc] = useState(true);
  const [confirmar, setConfirmar] = useState<Item | null>(null);

  const { data, isLoading } = useQuery<Resp>({
    queryKey: ['cat-calibracion', desde, hasta, minMuestras, tolerancia],
    queryFn: () => catRpc<Resp>('cat_calibracion_analisis', { _desde: desde, _hasta: hasta, _min_muestras: minMuestras, _tolerancia: tolerancia }),
  });
  const { data: hist } = useQuery<Hist[]>({
    queryKey: ['cat-calibracion-hist'],
    queryFn: () => catRpc<Hist[]>('cat_calibracion_historial', { _limite: 30 }),
  });

  const aplicar = useMutation({
    mutationFn: (it: Item) => catRpc('cat_calibracion_aplicar', {
      _componente_id: it.componente_id, _factor: it.factor_sugerido,
      _motivo: `consumo real ${desde}…${hasta} (ratio ${it.ratio})`, _desde: desde, _hasta: hasta,
    }),
    onSuccess: (_r, it) => { invalidateCatering(qc); void qc.invalidateQueries({ queryKey: ['cat-calibracion-hist'] }); setConfirmar(null); toast.success(`Ficha calibrada: ${it.material} ×${it.factor_sugerido}`); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const r = data?.resumen;
  const items = (data?.items ?? []).filter((i) => !soloAcc || i.semaforo !== 'ok');

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><SlidersHorizontal className="h-4 w-4" /> Calibración de fichas</CardTitle>
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div><label className="block text-xs text-muted-foreground">Desde</label><input type="date" className={`${fieldCls} w-40`} value={desde} onChange={(e) => setDesde(e.target.value)} /></div>
            <div><label className="block text-xs text-muted-foreground">Hasta</label><input type="date" className={`${fieldCls} w-40`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></div>
            <div><label className="block text-xs text-muted-foreground">Mín. órdenes</label><input type="number" min={1} className={`${fieldCls} w-24`} value={minMuestras} onChange={(e) => setMinMuestras(Number(e.target.value) || 1)} /></div>
            <div><label className="block text-xs text-muted-foreground">Tolerancia %</label><input type="number" min={0} step={0.5} className={`${fieldCls} w-24`} value={tolerancia} onChange={(e) => setTolerancia(Number(e.target.value) || 0)} /></div>
            <label className="flex items-center gap-1 pb-2 text-xs text-muted-foreground"><input type="checkbox" checked={soloAcc} onChange={(e) => setSoloAcc(e.target.checked)} /> solo accionables</label>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="py-6 text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin" /> Analizando el histórico…</p>}

          {!isLoading && r && (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div className="rounded-md border p-2"><p className="text-[11px] uppercase text-muted-foreground">Materiales</p><p className="text-xl font-semibold">{num(r.materiales, 0)}</p><p className="text-[11px] text-muted-foreground">en fichas activas</p></div>
                <div className={`rounded-md border-2 p-2 ${r.a_calibrar > 0 ? SEM.calibrar.cls : SEM.ok.cls}`}><p className="text-[11px] uppercase text-muted-foreground">A calibrar</p><p className={`text-xl font-semibold ${r.a_calibrar > 0 ? SEM.calibrar.txt : ''}`}>{num(r.a_calibrar, 0)}</p><p className="text-[11px] text-muted-foreground">desvío consistente</p></div>
                <div className={`rounded-md border-2 p-2 ${r.a_revisar > 0 ? SEM.revisar.cls : SEM.ok.cls}`}><p className="text-[11px] uppercase text-muted-foreground">A revisar</p><p className={`text-xl font-semibold ${r.a_revisar > 0 ? SEM.revisar.txt : ''}`}>{num(r.a_revisar, 0)}</p><p className="text-[11px] text-muted-foreground">desvío disperso</p></div>
                <div className="rounded-md border p-2"><p className="text-[11px] uppercase text-muted-foreground">Impacto teórico</p><p className={`text-xl font-semibold ${r.impacto_total < 0 ? 'text-emerald-600' : r.impacto_total > 0 ? 'text-red-600' : ''}`}>{money(r.impacto_total, 2)}</p><p className="text-[11px] text-muted-foreground">costo mal fichado</p></div>
              </div>

              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-1 pr-2">Material</th>
                    <th className="py-1 pr-2 text-right">Órdenes</th>
                    <th className="py-1 pr-2 text-right">Plan → Real</th>
                    <th className="py-1 pr-2 text-right">Desvío</th>
                    <th className="py-1 pr-2 text-right">Impacto $</th>
                    <th className="py-1 pr-2 text-right">Fichas</th>
                    <th className="py-1 pr-2">Estado</th>
                    <th className="py-1 pr-2"></th>
                  </tr></thead>
                  <tbody>
                    {items.map((i) => {
                      const s = SEM[i.semaforo];
                      const baja = i.desvio_pct < 0;
                      return (
                        <tr key={i.componente_id} className="border-b align-top last:border-0">
                          <td className="py-1.5 pr-2 font-medium">{i.material}<span className="block text-[11px] text-muted-foreground">{i.unidad}</span></td>
                          <td className="py-1.5 pr-2 text-right font-mono">{num(i.ordenes, 0)}<span className="block text-[11px] text-muted-foreground">{i.n_desvio} c/desvío</span></td>
                          <td className="py-1.5 pr-2 text-right font-mono">{num(i.plan_total, 1)} → {num(i.real_total, 1)}</td>
                          <td className={`py-1.5 pr-2 text-right font-mono ${baja ? 'text-emerald-600' : 'text-red-600'}`}>
                            {baja ? <TrendingDown className="inline h-3 w-3" /> : <TrendingUp className="inline h-3 w-3" />} {i.desvio_pct > 0 ? '+' : ''}{num(i.desvio_pct, 1)}%
                            <span className="block text-[11px] text-muted-foreground">×{i.factor_sugerido != null ? num(i.factor_sugerido, 3) : '—'}</span>
                          </td>
                          <td className={`py-1.5 pr-2 text-right font-mono ${i.impacto_costo < 0 ? 'text-emerald-600' : i.impacto_costo > 0 ? 'text-red-600' : ''}`}>{money(i.impacto_costo, 2)}</td>
                          <td className="py-1.5 pr-2 text-right font-mono" title={(i.recetas ?? []).join(', ')}>{num(i.n_lineas, 0)}</td>
                          <td className="py-1.5 pr-2"><span className={`inline-block rounded-full border-2 px-2 py-0.5 text-[11px] font-semibold ${s.cls} ${s.txt}`}>{s.label}</span></td>
                          <td className="py-1.5 pr-2 text-right">
                            {i.semaforo !== 'ok' && i.factor_sugerido != null && (
                              <Button size="sm" variant={i.semaforo === 'calibrar' ? 'default' : 'outline'} onClick={() => setConfirmar(i)}>Calibrar</Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {!items.length && <tr><td colSpan={8} className="py-3 text-sm text-muted-foreground">No hay materiales {soloAcc ? 'accionables' : ''} en el período. {soloAcc && 'Destilda "solo accionables" para ver todos.'}</td></tr>}
                  </tbody>
                </table>
              </div>
              <p className="pt-2 text-[11px] text-muted-foreground">
                El desvío compara el consumo real registrado contra el teórico de la ficha (la <b>cantidad</b>, que es la que cuesta).
                <b> Desvío −</b> = la ficha sobreestima (costo teórico inflado); <b>desvío +</b> = subestima (el food cost real es mayor).
                Se marca <b>Calibrar</b> solo con desvío consistente entre varias órdenes; <b>Revisar</b> si es disperso (un outlier). Calibrar ajusta la cantidad de todas las fichas que usan el material y queda en el historial.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Confirmación de calibración */}
      {confirmar && (
        <Card className="border-primary">
          <CardContent className="flex flex-wrap items-center gap-3 py-3">
            <div className="text-sm">
              Calibrar <b>{confirmar.material}</b> ×<b>{num(confirmar.factor_sugerido ?? 1, 3)}</b> en <b>{confirmar.n_lineas}</b> ficha(s)
              {confirmar.recetas?.length ? <span className="text-muted-foreground"> ({confirmar.recetas.slice(0, 4).join(', ')}{confirmar.recetas.length > 4 ? '…' : ''})</span> : null}.
              <span className="block text-xs text-muted-foreground">{confirmar.recomendacion}</span>
            </div>
            <div className="ml-auto flex gap-2">
              <Button size="sm" disabled={aplicar.isPending} onClick={() => aplicar.mutate(confirmar)} className="gap-1"><Check className="h-4 w-4" /> Aplicar</Button>
              <Button size="sm" variant="outline" onClick={() => setConfirmar(null)} className="gap-1"><X className="h-4 w-4" /> Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historial */}
      {!!hist?.length && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm"><History className="h-4 w-4" /> Historial de calibraciones</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-1 pr-2">Fecha</th><th className="py-1 pr-2">Material</th>
                  <th className="py-1 pr-2 text-right">Factor</th><th className="py-1 pr-2 text-right">Fichas</th>
                  <th className="py-1 pr-2 text-right">Órdenes</th><th className="py-1 pr-2">Motivo</th><th className="py-1 pr-2">Usuario</th>
                </tr></thead>
                <tbody>
                  {hist.map((h) => (
                    <tr key={h.id} className="border-b last:border-0">
                      <td className="py-1.5 pr-2 whitespace-nowrap">{fmtFecha(h.fecha)}</td>
                      <td className="py-1.5 pr-2 font-medium">{h.material}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">×{num(h.factor, 3)}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{num(h.lineas, 0)}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{num(h.ordenes, 0)}</td>
                      <td className="py-1.5 pr-2 text-xs text-muted-foreground">{h.motivo ?? '—'}</td>
                      <td className="py-1.5 pr-2 text-xs text-muted-foreground">{h.usuario ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
